import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { MagicCard } from "../ui/magic-card";
import ImagePlaceholder from "../ui/image-placeholder";
import { cn } from "@/lib";

interface TestimonialCardProps {
  quote: string;
  author: string;
  role?: string;
  company?: string;
  image?: string;
  rating?: number; // 1-5
  variant?: "default" | "simple" | "detailed";
  className?: string;
  animated?: boolean;
  index?: number;
}

const TestimonialCard = ({
  quote,
  author,
  role,
  company,
  image,
  rating = 5,
  variant = "default",
  className,
  animated = true,
  index = 0,
}: TestimonialCardProps) => {
  const renderRating = () => (
    <div className="flex items-center mb-4" data-oid="r:725uo">
      {[...Array(5)].map((_, i) => (
        <motion.div
          key={i}
          initial={animated ? { opacity: 0, rotate: -180 } : {}}
          whileInView={
            animated ? { opacity: i < rating ? 1 : 0.3, rotate: 0 } : {}
          }
          transition={animated ? { delay: i * 0.1, duration: 0.3 } : {}}
          viewport={{ once: true }}
          data-oid="xgw01vr"
        >
          <Star
            className={cn(
              "w-5 h-5",
              i < rating ? "text-yellow-400 fill-current" : "text-gray-300",
            )}
            data-oid="wfuqycc"
          />
        </motion.div>
      ))}
    </div>
  );

  if (variant === "simple") {
    return (
      <motion.div
        className={cn("text-center p-6", className)}
        initial={animated ? { opacity: 0, y: 20 } : {}}
        whileInView={animated ? { opacity: 1, y: 0 } : {}}
        transition={animated ? { duration: 0.5, delay: index * 0.2 } : {}}
        viewport={{ once: true }}
        data-oid="hu1:3hu"
      >
        {rating > 0 && renderRating()}
        <blockquote
          className="text-lg md:text-xl font-medium mb-4 italic"
          data-oid="d-_w2zu"
        >
          "{quote}"
        </blockquote>
        <div className="font-semibold" data-oid="_ja9:gl">
          {author}
        </div>
        {(role || company) && (
          <div className="text-sm text-muted-foreground" data-oid="tk_j.4_">
            {role}
            {company ? ` at ${company}` : ""}
          </div>
        )}
      </motion.div>
    );
  }

  return (
    <motion.div
      className="h-full"
      initial={animated ? { opacity: 0, y: 20 } : {}}
      whileInView={animated ? { opacity: 1, y: 0 } : {}}
      transition={animated ? { duration: 0.5, delay: index * 0.2 } : {}}
      viewport={{ once: true }}
      data-oid="9fvi:xv"
    >
      <MagicCard
        gradientFrom="#3b82f6"
        gradientTo="#8b5cf6"
        gradientColor="rgba(59,130,246,0.05)"
        className={cn(
          "p-6 rounded-xl border bg-card/50 backdrop-blur-sm h-full",
          className,
        )}
        data-oid=":0:s0db"
      >
        {rating > 0 && renderRating()}

        <p className="text-muted-foreground mb-4 italic" data-oid="665wiku">
          "{quote}"
        </p>

        <div className="flex items-center gap-3" data-oid="_wlu_nm">
          <ImagePlaceholder
            src={image}
            alt={author}
            type="avatar"
            className="w-10 h-10 rounded-full"
            data-oid="qn2r8mo"
          />

          <div data-oid=".lsrm3f">
            <p className="font-semibold" data-oid="kmi3xto">
              {author}
            </p>
            {(role || company) && (
              <p className="text-sm text-muted-foreground" data-oid="tvh8:l4">
                {role}
                {company ? ` at ${company}` : ""}
              </p>
            )}
          </div>
        </div>
      </MagicCard>
    </motion.div>
  );
};

export default TestimonialCard;
